import { useTranslation } from "react-i18next";
import type { ReviewRowState } from "../../lib/rosterImportReview";
import { reviewRowIsComplete } from "../../lib/rosterImportReview";
import { usePositionReferenceOptions } from "../../hooks/usePositionReferenceOptions";
import type {
  RosterImportMatchStatus,
  RosterImportRowAction,
} from "../../lib/rosterImportApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

/** Radix `Select.Item` forbids an empty-string value. */
const UNSET = "__unset__";

const STATUS_BADGE: Record<RosterImportMatchStatus, string> = {
  new: "bg-blue-100 text-blue-800",
  on_team: "bg-gray-100 text-gray-700",
  exists_elsewhere: "bg-amber-100 text-amber-800",
  ambiguous: "bg-purple-100 text-purple-800",
};

export interface ReviewTableProps {
  rows: ReviewRowState[];
  onChange: (rowId: string, next: ReviewRowState) => void;
}

export default function ReviewTable({ rows, onChange }: ReviewTableProps) {
  const { t } = useTranslation("admin");

  return (
    <div className="overflow-x-auto">
      <table
        className="min-w-full divide-y divide-gray-200"
        data-testid="roster-import-review-table"
        aria-label={t("rosterImport.reviewTitle")}
      >
        <thead className="table-header">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("name")}
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("position")}
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("rosterImport.jerseyNumberOptional")}
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("rosterImport.matchStatus")}
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("rosterImport.action")}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <ReviewTableRow
              key={row.rowId}
              row={row}
              onChange={(next) => onChange(row.rowId, next)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReviewTableRow({
  row,
  onChange,
}: {
  row: ReviewRowState;
  onChange: (next: ReviewRowState) => void;
}) {
  const { t } = useTranslation("admin");
  const positionOptions = usePositionReferenceOptions(
    row.position || undefined,
  );
  const flatPositions = positionOptions.groupOrder.flatMap(
    (group) => positionOptions.grouped[group],
  );
  const incomplete = !reviewRowIsComplete(row);

  return (
    <tr
      data-testid={`roster-import-row-${row.rowId}`}
      className={incomplete ? "bg-red-50/40" : ""}
    >
      <td className="px-3 py-2 align-top">
        <input
          type="text"
          value={row.name}
          onChange={(e) => onChange({ ...row, name: e.target.value })}
          data-testid={`roster-import-row-name-${row.rowId}`}
          className="input w-full min-w-[10rem]"
        />
      </td>
      <td className="px-3 py-2 align-top">
        <Select
          value={row.position === "" ? UNSET : row.position}
          onValueChange={(next) =>
            onChange({ ...row, position: next === UNSET ? "" : next })
          }
        >
          <SelectTrigger
            data-testid={`roster-import-row-position-${row.rowId}`}
            className="w-32"
          >
            <SelectValue placeholder={t("quickCreatePlayer.selectPosition")}>
              {flatPositions.find((opt) => opt.code === row.position)?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNSET}>
              {t("quickCreatePlayer.selectPosition")}
            </SelectItem>
            {flatPositions.map((opt) => (
              <SelectItem key={opt.code} value={opt.code}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2 align-top">
        <input
          type="number"
          min={1}
          max={99}
          value={row.jerseyNumber}
          onChange={(e) => onChange({ ...row, jerseyNumber: e.target.value })}
          data-testid={`roster-import-row-jersey-${row.rowId}`}
          className="input w-20"
        />
      </td>
      <td className="px-3 py-2 align-top">
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[row.matchStatus]}`}
          data-testid={`roster-import-row-status-${row.rowId}`}
        >
          {t(`rosterImport.matchStatuses.${row.matchStatus}`)}
        </span>
        {row.candidates.length > 0 && (
          <p className="mt-1 text-[11px] text-gray-500">
            {row.candidates
              .map(
                (candidate) =>
                  `${candidate.name}${
                    candidate.team_names.length > 0
                      ? ` (${candidate.team_names.join(", ")})`
                      : ""
                  }`,
              )
              .join(" · ")}
          </p>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex flex-col gap-1">
          <Select
            value={row.action}
            onValueChange={(next) =>
              onChange({
                ...row,
                action: next as RosterImportRowAction,
                linkedPlayerId: next === "link" ? row.linkedPlayerId : "",
              })
            }
          >
            <SelectTrigger
              data-testid={`roster-import-row-action-${row.rowId}`}
              className="w-28"
            >
              <SelectValue>
                {t(`rosterImport.actions.${row.action}`)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="create">
                {t("rosterImport.actions.create")}
              </SelectItem>
              <SelectItem value="link" disabled={row.candidates.length === 0}>
                {t("rosterImport.actions.link")}
              </SelectItem>
              <SelectItem value="skip">
                {t("rosterImport.actions.skip")}
              </SelectItem>
            </SelectContent>
          </Select>
          {row.action === "link" && (
            <Select
              value={row.linkedPlayerId === "" ? UNSET : row.linkedPlayerId}
              onValueChange={(next) =>
                onChange({
                  ...row,
                  linkedPlayerId: next === UNSET ? "" : next,
                })
              }
            >
              <SelectTrigger
                data-testid={`roster-import-row-candidate-${row.rowId}`}
                className={`w-48 ${incomplete ? "border-red-400" : ""}`}
              >
                <SelectValue placeholder={t("rosterImport.pickCandidate")}>
                  {
                    row.candidates.find(
                      (c) => c.player_id === row.linkedPlayerId,
                    )?.name
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNSET}>
                  {t("rosterImport.pickCandidate")}
                </SelectItem>
                {row.candidates.map((candidate) => (
                  <SelectItem
                    key={candidate.player_id}
                    value={candidate.player_id}
                    data-testid={`roster-import-row-candidate-${row.rowId}-option-${candidate.player_id}`}
                  >
                    {candidate.name}
                    {candidate.team_names.length > 0
                      ? ` — ${candidate.team_names.join(", ")}`
                      : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </td>
    </tr>
  );
}
